/**
 * UTM discount codes (IT-2464 / IT-2929): utm_term → metaobject match → Shopify discount + strike prices.
 */
(function () {
  'use strict';

  const ACTIVE_KEY = 'am_utm_active';
  const DISMISSED_KEY = 'am_utm_dismissed';
  const CODE_TOKEN = '__CODE__';
  const DEFAULT_BANNER = '#116a29';
  const DEFAULT_ACCENT = '#079545';
  const PREFIX_KEYS = ['OMG', 'OFF', 'YAS', 'FAL', 'CAL', 'HAP', 'SNO', 'CBA', 'CBG'];
  const PREFIX_DATASET = {
    OMG: 'discountOmg',
    OFF: 'discountOff',
    YAS: 'discountYas',
    FAL: 'discountFal',
    CAL: 'discountCal',
    HAP: 'discountHap',
    SNO: 'discountSno',
    CBA: 'discountCba',
    CBG: 'discountCbg',
  };

  /** @type {ActiveMatch | null} */
  let activeMatch = null;
  /** @type {Config | null} */
  let activeConfig = null;
  let gridsObserved = false;

  /**
   * @typedef {{ prefix?: string, codes?: unknown, percent?: number, colorHex?: string, collectionIds?: Array<number|string>, collectionHandles?: Array<string> }} UtmEntry
   * @typedef {{ code: string, prefix: string, percent: number, colorHex?: string, collectionIds: string[], collectionHandles: string[], useProductPrefix: boolean }} ActiveMatch
   * @typedef {{ entries: UtmEntry[], moneyFormat: string, translations: Record<string, string>, currentCollectionId?: number|string|null, currentCollectionHandle?: string|null }} Config
   */

  /**
   * @returns {Config | null}
   */
  function readConfig() {
    const node = document.querySelector('[data-am-utm-discount-config]');
    if (!(node instanceof HTMLScriptElement) || !node.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return null;
    }
  }

  /**
   * @param {unknown} raw
   * @returns {string}
   */
  function normalizeHex(raw) {
    const value = String(raw || '').trim();
    if (!value) return '';
    return value.charAt(0) === '#' ? value : '#' + value;
  }

  /**
   * @param {ActiveMatch} match
   */
  function applyUtmColors(match) {
    const hex = normalizeHex(match.colorHex) || DEFAULT_BANNER;
    document.documentElement.style.setProperty('--am-utm-banner-bg', hex);
    document.documentElement.style.setProperty('--am-utm-accent', hex);
  }

  function clearUtmColors() {
    document.documentElement.style.removeProperty('--am-utm-banner-bg');
    document.documentElement.style.removeProperty('--am-utm-accent');
  }

  /**
   * @param {string} name
   * @returns {string}
   */
  function getCookie(name) {
    const match = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
    );
    return match && match[1] ? decodeURIComponent(match[1]) : '';
  }

  /**
   * @param {string} name
   */
  function clearCookie(name) {
    document.cookie = name + '=; Path=/; Max-Age=0';
    document.cookie = name + '=; Path=/; Max-Age=0; SameSite=Lax';
  }

  /**
   * @param {string} haystack
   * @param {string} token
   * @param {string} value
   * @returns {string}
   */
  function replaceToken(haystack, token, value) {
    return String(haystack || '').split(token).join(value);
  }

  /**
   * @param {unknown} raw
   * @returns {string[]}
   */
  function normalizeCodes(raw) {
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) {
      /** @type {string[]} */
      const out = [];
      raw.forEach(function (item) {
        normalizeCodes(item).forEach(function (code) {
          out.push(code);
        });
      });
      return out;
    }
    if (typeof raw === 'object' && raw !== null && 'value' in raw) {
      return normalizeCodes(/** @type {{ value: unknown }} */ (raw).value);
    }
    return String(raw)
      .split(/[,•|;/\n]+/)
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  /**
   * @param {unknown} id
   * @returns {string}
   */
  function normalizeCollectionId(id) {
    const value = String(id || '');
    const gid = value.match(/Collection\/(\d+)/i);
    if (gid && gid[1]) return gid[1];
    return value;
  }

  /**
   * @param {UtmEntry} entry
   * @returns {string[]}
   */
  function entryCollectionIds(entry) {
    const raw = entry.collectionIds;
    if (!Array.isArray(raw)) return [];
    return raw.map(normalizeCollectionId).filter(Boolean);
  }

  /**
   * @param {UtmEntry} entry
   * @returns {string[]}
   */
  function entryCollectionHandles(entry) {
    const raw = entry.collectionHandles;
    if (!Array.isArray(raw)) return [];
    return raw
      .map(function (handle) {
        return String(handle || '')
          .trim()
          .toLowerCase();
      })
      .filter(Boolean);
  }

  /**
   * @param {string} candidate
   * @param {UtmEntry[]} entries
   * @returns {ActiveMatch | null}
   */
  function matchEntry(candidate, entries) {
    const code = String(candidate || '')
      .trim()
      .toUpperCase();
    if (!code) return null;

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (!entry) continue;

      const prefix = String(entry.prefix || '')
        .trim()
        .toUpperCase();
      const codes = normalizeCodes(entry.codes).map(function (item) {
        return item.toUpperCase();
      });

      let matched = codes.indexOf(code) !== -1;
      if (!matched && prefix) {
        matched = code.indexOf(prefix) === 0;
      }
      if (!matched) continue;

      const useProductPrefix = prefix !== '' && PREFIX_KEYS.indexOf(prefix) !== -1;

      return {
        code: candidate.trim(),
        prefix: prefix,
        percent: Number(entry.percent) || 0,
        colorHex: String(entry.colorHex || ''),
        collectionIds: entryCollectionIds(entry),
        collectionHandles: entryCollectionHandles(entry),
        useProductPrefix: useProductPrefix,
      };
    }
    return null;
  }

  /**
   * @param {HTMLElement} el
   * @param {ActiveMatch} match
   * @returns {number}
   */
  function productDiscountPercent(el, match) {
    if (match.useProductPrefix && match.prefix) {
      const datasetKey = PREFIX_DATASET[match.prefix];
      if (datasetKey && el.dataset[datasetKey]) {
        const value = parseFloat(String(el.dataset[datasetKey]));
        if (!isNaN(value) && value > 0) return value;
      }
      return 0;
    }
    return match.percent;
  }

  /**
   * @returns {string}
   */
  function cartUpdateUrl() {
    const routes = window.Theme && window.Theme.routes;
    const url = (routes && routes.cart_update_url) || '/cart/update';
    return url.indexOf('.js') === -1 ? url + '.js' : url;
  }

  /**
   * @returns {string}
   */
  function shopRoot() {
    const routes = window.Theme && window.Theme.routes;
    const root = (routes && /** @type {{ root?: string }} */ (routes).root) || '/';
    return String(root).replace(/\/$/, '') || '';
  }

  /**
   * @param {string} code
   * @returns {string}
   */
  function discountApplyUrl(code) {
    return shopRoot() + '/discount/' + encodeURIComponent(code);
  }

  /**
   * @param {string} code
   * @param {string} redirectPath
   */
  function redirectToDiscount(code, redirectPath) {
    const encoded = encodeURIComponent(redirectPath || '/');
    window.location.href = discountApplyUrl(code) + '?redirect=' + encoded;
  }

  /**
   * @returns {Promise<unknown>}
   */
  function clearCartDiscount() {
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        discount: '',
        attributes: {
          utm_discount_code: '',
        },
      }),
    }).catch(function () {
      return null;
    });
  }

  /**
   * @param {number} cents
   * @param {string} format
   * @returns {string}
   */
  function formatMoney(cents, format) {
    const amount = (Math.round(cents) / 100).toFixed(2);
    const withComma = amount.replace('.', ',');
    const withDot = amount;
    return String(format || '{{amount}}')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount\s*\}\}/g, withDot);
  }

  /**
   * @param {string} handle
   * @returns {boolean}
   */
  function isSaleCollectionHandle(handle) {
    const value = String(handle || '')
      .trim()
      .toLowerCase();
    if (!value) return false;
    return (
      value === 'sale' ||
      value === 'outlet' ||
      value === 'abverkauf' ||
      value.indexOf('sale-') === 0 ||
      value.indexOf('outlet-') === 0 ||
      value.indexOf('abverkauf-') === 0
    );
  }

  /**
   * @param {HTMLElement} card
   * @param {ActiveMatch} match
   * @returns {boolean}
   */
  function cardInAllowlist(card, match) {
    const scoped = match.collectionIds.length > 0 || match.collectionHandles.length > 0;
    if (!scoped) return true;

    const pageId =
      activeConfig && activeConfig.currentCollectionId != null
        ? normalizeCollectionId(activeConfig.currentCollectionId)
        : '';
    const pageHandle =
      activeConfig && activeConfig.currentCollectionHandle
        ? String(activeConfig.currentCollectionHandle).trim().toLowerCase()
        : '';

    if (pageId && match.collectionIds.indexOf(pageId) !== -1) return true;
    if (pageHandle && match.collectionHandles.indexOf(pageHandle) !== -1) return true;

    const ids = (card.getAttribute('data-utm-collections') || '')
      .split(',')
      .map(normalizeCollectionId)
      .filter(Boolean);
    if (
      match.collectionIds.some(function (id) {
        return ids.indexOf(normalizeCollectionId(id)) !== -1;
      })
    ) {
      return true;
    }

    const handles = (card.getAttribute('data-utm-collection-handles') || '')
      .split(',')
      .map(function (handle) {
        return handle.trim().toLowerCase();
      })
      .filter(Boolean);
    return match.collectionHandles.some(function (handle) {
      return handles.indexOf(handle) !== -1;
    });
  }

  /**
   * @param {ActiveMatch} match
   * @param {string} moneyFormat
   * @param {Record<string, string>} translations
   */
  function applyStrikePrices(match, moneyFormat, translations) {
    const cards = document.querySelectorAll(
      '.product-card[data-utm-price], product-card[data-utm-price], product-price[data-utm-price]'
    );
    const appliedSuffix = translations.applied || 'angewendet';

    cards.forEach(function (card) {
      if (!(card instanceof HTMLElement)) return;
      if (card.getAttribute('data-utm-exclude') === 'true') {
        revertCard(card);
        return;
      }
      if (card.classList.contains('product-card--mix-match')) {
        revertCard(card);
        return;
      }
      if (!cardInAllowlist(card, match)) {
        revertCard(card);
        return;
      }

      const scoped = match.collectionIds.length > 0 || match.collectionHandles.length > 0;
      if (!scoped) {
        const handles = (card.getAttribute('data-utm-collection-handles') || '').split(',');
        if (handles.some(isSaleCollectionHandle)) {
          revertCard(card);
          return;
        }
      }

      const percent = productDiscountPercent(card, match);
      if (!percent || percent <= 0) {
        revertCard(card);
        return;
      }
      const discountRate = percent > 1 ? percent / 100 : percent;

      const base = Number(card.getAttribute('data-utm-price')) || 0;
      if (base <= 0) return;

      const discounted = Math.round(base * (1 - discountRate));
      const saleText = formatMoney(discounted, moneyFormat);
      const compareText = formatMoney(base, moneyFormat);

      const saleNodes = card.querySelectorAll(
        '.product-card__info p.price:not(.price--compare), .price-display-mobile p.price:not(.price--compare), .price--sale, .price__regular .price, .price-item--sale, product-price .price:not(.price--compare)'
      );
      saleNodes.forEach(function (node) {
        if (!(node instanceof HTMLElement)) return;
        if (!node.dataset.utmOriginalPrice) {
          node.dataset.utmOriginalPrice = node.textContent || '';
        }
        if (!node.classList.contains('price--sale')) {
          node.dataset.utmAddedSaleClass = 'true';
        }
        node.textContent = saleText;
        node.classList.add('price--sale', 'am-utm-discount-price');
      });

      let compareNode = card.querySelector(
        '.product-card__info .price--compare, .price-display-mobile .price--compare, .compare-at-price, product-price .compare-at-price'
      );
      if (!compareNode) {
        const firstSale = card.querySelector('.am-utm-discount-price');
        if (firstSale) {
          compareNode = document.createElement('p');
          compareNode.className = 'price price--compare compare-at-price am-utm-discount-compare';
          compareNode.dataset.utmInjected = 'true';
          const priceRow = card.querySelector('.price-display-mobile');
          const inclTax = priceRow && priceRow.querySelector('.incl-tax');
          if (priceRow && inclTax) {
            priceRow.insertBefore(compareNode, inclTax);
          } else {
            firstSale.insertAdjacentElement('afterend', compareNode);
          }
        }
      }
      if (compareNode instanceof HTMLElement) {
        if (!compareNode.dataset.utmOriginalPrice) {
          compareNode.dataset.utmOriginalPrice = compareNode.textContent || '';
        }
        compareNode.textContent = compareText;
        compareNode.classList.add('am-utm-discount-compare');
      }

      if (!card.querySelector('[data-am-utm-applied-label]')) {
        const label = document.createElement('p');
        label.className = 'am-utm-discount-applied';
        label.dataset.amUtmAppliedLabel = 'true';
        const strong = document.createElement('strong');
        strong.textContent = match.code;
        label.appendChild(strong);
        label.appendChild(document.createTextNode(' ' + appliedSuffix));

        const info =
          card.querySelector('.product-plp-info') ||
          card.querySelector('.product-title') ||
          card.querySelector('.product-card__heading') ||
          card.querySelector('[role="heading"]');
        if (info) {
          info.insertAdjacentElement('afterend', label);
        } else {
          const priceWrap =
            card.querySelector('.product-card__info') ||
            card.querySelector('.product-card__content') ||
            card.querySelector('[ref="priceContainer"]');
          if (priceWrap) {
            priceWrap.insertBefore(label, priceWrap.firstChild);
          }
        }
      }

      card.setAttribute('data-utm-active', 'true');
    });
  }

  /**
   * @param {Element} card
   */
  function revertCard(card) {
    if (!(card instanceof HTMLElement)) return;
    card.querySelectorAll('[data-utm-original-price]').forEach(function (node) {
      if (!(node instanceof HTMLElement)) return;
      node.textContent = node.dataset.utmOriginalPrice || '';
      if (node.dataset.utmAddedSaleClass === 'true') {
        node.classList.remove('price--sale');
        delete node.dataset.utmAddedSaleClass;
      }
      node.classList.remove('am-utm-discount-price', 'am-utm-discount-compare');
      delete node.dataset.utmOriginalPrice;
    });
    card.querySelectorAll('[data-utm-injected="true"]').forEach(function (node) {
      node.remove();
    });
    card.querySelectorAll('[data-am-utm-applied-label]').forEach(function (node) {
      node.remove();
    });
    card.removeAttribute('data-utm-active');
  }

  function revertAllCards() {
    document.querySelectorAll('[data-utm-active="true"]').forEach(revertCard);
    document.body.removeAttribute('data-utm-discount');
  }

  /**
   * @param {ActiveMatch} match
   * @param {Record<string, string>} translations
   */
  function showBanner(match, translations) {
    const banner = document.querySelector('[data-am-utm-discount-banner]');
    if (!(banner instanceof HTMLElement)) return;

    const safeCode = String(match.code).replace(/[<>]/g, '');
    const message = banner.querySelector('[data-am-utm-discount-message]');
    if (message instanceof HTMLElement && translations.banner) {
      message.innerHTML = replaceToken(
        replaceToken(translations.banner, CODE_TOKEN, '<strong>' + safeCode + '</strong>'),
        '{{ code }}',
        '<strong>' + safeCode + '</strong>'
      );
    }
    banner.hidden = false;
  }

  function hideBanner() {
    const banner = document.querySelector('[data-am-utm-discount-banner]');
    if (banner instanceof HTMLElement) banner.hidden = true;
  }

  /**
   * @param {ActiveMatch} match
   * @param {Config} config
   */
  function activateUi(match, config) {
    activeMatch = match;
    activeConfig = config;
    document.body.setAttribute('data-utm-discount', match.code);
    applyUtmColors(match);
    showBanner(match, config.translations);
    applyStrikePrices(match, config.moneyFormat, config.translations);
  }

  function dismissDiscount() {
    try {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
      sessionStorage.removeItem(ACTIVE_KEY);
    } catch (_e) {
      // ignore
    }
    activeMatch = null;
    activeConfig = null;
    clearCookie('discount_code');
    clearCartDiscount();
    hideBanner();
    revertAllCards();
    clearUtmColors();
  }

  function bindBanner() {
    const banner = document.querySelector('[data-am-utm-discount-banner]');
    if (!(banner instanceof HTMLElement) || banner.dataset.bound === 'true') return;
    banner.dataset.bound = 'true';

    const closeBtn = banner.querySelector('[data-am-utm-discount-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', dismissDiscount);
    }
  }

  function observeProductGrids() {
    if (gridsObserved) return;
    gridsObserved = true;
    let timer = 0;
    const rerun = function () {
      if (!activeMatch || !activeConfig) return;
      applyStrikePrices(activeMatch, activeConfig.moneyFormat, activeConfig.translations);
    };
    const observer = new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(rerun, 80);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    ['collection:updated', 'product-grid:updated', 'facets:updated'].forEach(function (eventName) {
      document.addEventListener(eventName, rerun);
    });
  }

  /**
   * @param {string} utmTerm
   * @param {Config} config
   */
  function handleUtmTermLanding(utmTerm, config) {
    const match = matchEntry(utmTerm, config.entries);
    if (!match) return;

    try {
      sessionStorage.removeItem(DISMISSED_KEY);
      sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(match));
    } catch (_e) {
      // ignore
    }

    const cleanSearch = window.location.search
      .replace(/([?&])utm_term=[^&]*&?/, '$1')
      .replace(/[?&]$/, '');
    const redirectPath = window.location.pathname + (cleanSearch ? cleanSearch : '');
    redirectToDiscount(match.code, redirectPath || '/');
  }

  function initFromSession() {
    const config = readConfig();
    if (!config || !Array.isArray(config.entries) || config.entries.length === 0) return;

    try {
      if (sessionStorage.getItem(DISMISSED_KEY) === 'true') return;
    } catch (_e) {
      return;
    }

    /** @type {ActiveMatch | null} */
    let match = null;
    try {
      const stored = sessionStorage.getItem(ACTIVE_KEY);
      if (stored) match = JSON.parse(stored);
    } catch (_e2) {
      match = null;
    }

    if (!match || !match.code) return;

    bindBanner();
    observeProductGrids();
    activateUi(match, config);
  }

  function bootstrap() {
    const config = readConfig();
    if (!config || !Array.isArray(config.entries) || config.entries.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const utmTerm = params.get('utm_term');

    if (utmTerm) {
      handleUtmTermLanding(utmTerm, config);
      return;
    }

    initFromSession();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', bootstrap);
})();
